import { useEffect, useState } from "react";
import { api, money, formatApiError, API_BASE } from "@/lib/api";
import { Calculator, X, Send, Download, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal de cotización rápida — pensado para que la tripulación
 * cotice in-situ cuando un transeúnte/paciente les pregunta el costo.
 * Guarda el lead en `prospects` para seguimiento comercial.
 */
export default function QuickQuoteModal({ open, onClose }) {
    const [serviceType, setServiceType] = useState("TBL");
    const [serviceTypes, setServiceTypes] = useState([]);
    const [isForeign, setIsForeign] = useState(false);
    const [km, setKm] = useState(0);
    const [hours, setHours] = useState(0);
    const [preview, setPreview] = useState(null);
    const [prospect, setProspect] = useState({ name: "", phone: "", email: "" });
    const [saving, setSaving] = useState(false);
    const [savedQuote, setSavedQuote] = useState(null);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const r = await api.get("/service-types");
                if (r.data) {
                    // Normalizar: el endpoint devuelve {code, name, ...} en algunos casos label
                    setServiceTypes((r.data || []).map((s) => ({ ...s, label: s.label || s.name || s.code })));
                }
            } catch (e) { void e; }
        })();
    }, [open]);

    // Live preview de precio
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/pricing/preview", {
                    params: {
                        service_type_code: serviceType,
                        is_foreign: isForeign,
                        km: parseFloat(km) || 0,
                        hours: parseFloat(hours) || 0,
                    },
                });
                if (r.data) setPreview(r.data);
            } catch (e) { void e; }
        }, 250);
        return () => clearTimeout(t);
    }, [open, serviceType, isForeign, km, hours]);

    const reset = () => {
        setServiceType("TBL"); setIsForeign(false); setKm(0); setHours(0);
        setProspect({ name: "", phone: "", email: "" });
        setSavedQuote(null);
    };

    const close = () => { reset(); onClose(); };

    const saveAndShare = async (action) => {
        if (!preview) return toast.error("Esperando cálculo de precio");
        setSaving(true);
        try {
            const items = [{
                code: serviceType,
                description: serviceTypes.find(s => s.code === serviceType)?.label || serviceType,
                qty: 1,
                unit_price: preview.client_rate,
                total: preview.client_rate,
            }];
            const r = await api.post("/quotes", {
                client_id: null,
                prospect_name: prospect.name || null,
                prospect_phone: prospect.phone || null,
                prospect_email: prospect.email || null,
                service_type_code: serviceType,
                items,
                subtotal: preview.subtotal,
                iva: preview.iva,
                cash_no_invoice: false,
                payment_method: "factura",
                total: preview.total,
                source: "crew_quick",
                notes: `Cotización rápida generada desde móvil. ${isForeign ? "Foráneo " + km + "km. " : ""}${hours ? hours + " hrs. " : ""}`,
            });
            setSavedQuote(r.data);
            toast.success(`Cotización ${r.data.folio} guardada`);

            if (action === "whatsapp" && prospect.phone) {
                const msg = encodeURIComponent(
                    `Hola${prospect.name ? " " + prospect.name : ""}, te comparto cotización FindMed:\n` +
                    `Folio: ${r.data.folio}\n` +
                    `Servicio: ${items[0].description}\n` +
                    `Total: $${preview.total.toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN\n` +
                    `Vigencia: 7 días`
                );
                const phone = prospect.phone.replace(/[^0-9+]/g, "");
                window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
            } else if (action === "pdf") {
                window.open(`${API_BASE}/pdf/quote/${r.data.id}`, "_blank");
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-lg sm:rounded-sm max-w-lg w-full max-h-[92vh] overflow-y-auto" data-testid="quick-quote-modal">
                <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
                    <h3 className="font-display font-bold text-base flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-red-600" /> Cotización rápida
                    </h3>
                    <button onClick={close} data-testid="qq-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-4 space-y-3">
                    {/* Tipo de servicio */}
                    <div>
                        <label className="fm-label">Tipo de servicio</label>
                        <select
                            value={serviceType}
                            onChange={(e) => setServiceType(e.target.value)}
                            className="fm-input"
                            data-testid="qq-service-type"
                        >
                            {serviceTypes.map(s => (
                                <option key={s.code} value={s.code}>{s.code} · {s.label}</option>
                            ))}
                        </select>
                    </div>

                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isForeign}
                            onChange={(e) => setIsForeign(e.target.checked)}
                            data-testid="qq-foreign"
                        />
                        Servicio foráneo (cobra por km)
                    </label>

                    {isForeign && (
                        <div>
                            <label className="fm-label">Kilómetros</label>
                            <input type="number" min="0" value={km} onChange={(e) => setKm(e.target.value)} className="fm-input" data-testid="qq-km" />
                        </div>
                    )}

                    {(serviceType === "EVT" || serviceType === "TRH") && (
                        <div>
                            <label className="fm-label">Horas (cuando aplica facturación por hora)</label>
                            <input type="number" min="0" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} className="fm-input" data-testid="qq-hours" />
                        </div>
                    )}

                    {/* Datos del prospecto */}
                    <div className="border-t border-slate-200 pt-3">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                            Datos del prospecto (opcional, pero se queda en CRM)
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <input
                                placeholder="Nombre"
                                value={prospect.name}
                                onChange={(e) => setProspect({ ...prospect, name: e.target.value })}
                                className="fm-input col-span-2"
                                data-testid="qq-prospect-name"
                            />
                            <input
                                placeholder="Teléfono"
                                value={prospect.phone}
                                onChange={(e) => setProspect({ ...prospect, phone: e.target.value })}
                                className="fm-input"
                                inputMode="tel"
                                data-testid="qq-prospect-phone"
                            />
                            <input
                                placeholder="Email"
                                value={prospect.email}
                                onChange={(e) => setProspect({ ...prospect, email: e.target.value })}
                                className="fm-input"
                                inputMode="email"
                                data-testid="qq-prospect-email"
                            />
                        </div>
                    </div>

                    {/* Preview de precio — pegajoso */}
                    {preview && (
                        <div className="bg-slate-900 text-white p-4 rounded-sm">
                            <div className="flex items-baseline justify-between">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Total al cliente</span>
                                <span className="text-[10px] text-slate-400">IVA incluido</span>
                            </div>
                            <div className="font-mono font-black text-4xl tabular-nums mt-1">{money(preview.total)}</div>
                            <div className="grid grid-cols-2 gap-2 mt-3 text-[11px] text-slate-300 font-mono">
                                <div>Subtotal: {money(preview.subtotal)}</div>
                                <div>IVA 16%: {money(preview.iva)}</div>
                                {preview.is_night && <div className="text-amber-300">⏰ Horario nocturno</div>}
                                {preview.is_holiday && <div className="text-amber-300">🎄 Día feriado</div>}
                            </div>
                        </div>
                    )}

                    {/* Acciones */}
                    {!savedQuote ? (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <button
                                onClick={() => saveAndShare("save")}
                                disabled={saving || !preview}
                                className="fm-btn-primary justify-center"
                                data-testid="qq-save"
                            >
                                <Send className="w-3.5 h-3.5" /> Guardar
                            </button>
                            <button
                                onClick={() => saveAndShare("whatsapp")}
                                disabled={saving || !preview || !prospect.phone}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-sm font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
                                data-testid="qq-whatsapp"
                            >
                                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                            </button>
                            <button
                                onClick={() => saveAndShare("pdf")}
                                disabled={saving || !preview}
                                className="fm-btn-outline justify-center"
                                data-testid="qq-pdf"
                            >
                                <Download className="w-3.5 h-3.5" /> Guardar + PDF
                            </button>
                        </div>
                    ) : (
                        <div className="bg-emerald-50 border-l-4 border-l-emerald-500 p-3 space-y-2">
                            <div className="text-sm font-bold text-emerald-900">✅ Cotización guardada</div>
                            <div className="text-xs font-mono">{savedQuote.folio}</div>
                            <div className="flex flex-wrap gap-2">
                                <a href={`${API_BASE}/pdf/quote/${savedQuote.id}`} target="_blank" rel="noreferrer" className="fm-btn-outline text-xs">
                                    <Download className="w-3 h-3" /> Descargar PDF
                                </a>
                                {prospect.phone && (
                                    <a
                                        href={`https://wa.me/${prospect.phone.replace(/[^0-9+]/g, "")}?text=${encodeURIComponent(`Hola, te comparto cotización FindMed ${savedQuote.folio} por $${preview.total.toLocaleString("es-MX")} MXN.`)}`}
                                        target="_blank" rel="noreferrer"
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-sm font-bold text-xs uppercase tracking-wider flex items-center gap-2"
                                    >
                                        <MessageCircle className="w-3 h-3" /> WhatsApp
                                    </a>
                                )}
                                <button onClick={() => { reset(); }} className="fm-btn-outline text-xs">Nueva cotización</button>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-2">
                                {prospect.phone || prospect.email
                                    ? "📊 El prospecto se agregó al CRM. Cabina lo verá en /quotes y /prospects para dar seguimiento."
                                    : "💡 Tip: agrega teléfono/email del prospecto para que cabina pueda darle seguimiento."}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
